import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import {
  MANCHESTER_EVENTS,
  BRAGBOOQ_URL,
  TAG_LABELS,
  FILTER_LABELS,
  getUpcomingEvents,
  getPastEvents,
  getPickEvent,
  groupEventsByMonth,
  getMonthName,
  getShortMonthName,
  getWeekdayShort,
  parseDate,
  Event,
  EventTag,
} from '../../content/manchester-events';
import { products } from '../../config/products';
import { trackEvent } from '../../helpers';
import guideHero from '../../assets/guide-page-1.png';
import bragbooqDashboard from '../../assets/bragbooq.png';
import {
  PageWrapper,
  Wrap,
  Eyebrow,
  HeroSection,
  HeroTitle,
  HeroSub,
  HeroMeta,
  PickSection,
  PickDate,
  PickDay,
  PickMonth,
  PickLabel,
  Badge,
  PickTitle,
  PickDesc,
  PickWhere,
  ButtonLight,
  Button,
  TextLink,
  FiltersContainer,
  FiltersLabel,
  Chip,
  ChipCount,
  MonthSection,
  MonthHeading,
  EventsList,
  EventRow,
  EventWhen,
  EventTitle,
  EventStar,
  EventWhere,
  EventDesc,
  EventTags,
  Tag,
  TicketsButton,
  EmptyMessage,
  GuideCTASection,
  GuideCTATitle,
  GuideCTAText,
  GuideCTAList,
  GuideCTAItem,
  BuyRow,
  PriceBlock,
  PriceNow,
  PriceWas,
  PriceLabel,
  GuideImage,
  BragSection,
  BragInner,
  BragTitle,
  BragText,
  BragRow,
  BragTextLink,
  BragShot,
  FootNote,
  FootNoteTitle,
  FootNoteText,
  FootNoteSmall,
  SiteFooter,
  PastEventsSection,
  PastEventsHeader,
  PastEventsTitle,
  PastEventsToggle,
  PastEventsContent,
  PastEventRow,
} from './events.styles';

type FilterType = 'all' | 'past' | EventTag;

// Get the guide product for pricing
const guideProduct = products.find(p => p.slug === 'unhinged-guide');
const currentPrice = guideProduct?.currentPrice ?? 39;
const futurePrice = 69;

// Generate JSON-LD for events
const generateEventJsonLd = (events: Event[]) => {
  return events.map(event => {
    const startDate = parseDate(event.start);
    const endDate = event.end ? parseDate(event.end) : startDate;

    const locationParts = event.where.split(',').map(s => s.trim());
    const venueName = locationParts[0];
    const addressLocality = locationParts.length > 1 ? locationParts[locationParts.length - 1] : 'Manchester';

    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.title,
      description: event.desc,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'Place',
        name: venueName,
        address: {
          '@type': 'PostalAddress',
          addressLocality: addressLocality,
          addressCountry: 'GB'
        }
      },
      url: event.url,
      ...(event.free && { isAccessibleForFree: true }),
    };
  });
};

// Format event date display
const formatEventDate = (event: Event): string => {
  const start = parseDate(event.start);
  const end = event.end ? parseDate(event.end) : null;

  if (!end) {
    return `${start.getDate()}`;
  }

  return `${start.getDate()}\u2013${end.getDate()}`;
};

const formatEventDateMeta = (event: Event): string => {
  const start = parseDate(event.start);
  const end = event.end ? parseDate(event.end) : null;
  const wd = getWeekdayShort(start);
  const mon = getShortMonthName(start);

  if (!end) {
    const timeStr = event.time ? `<br/>${event.time}` : '';
    return `${mon} \u00B7 ${wd}${timeStr}`;
  }

  const sameMonth = start.getMonth() === end.getMonth();
  const emon = getShortMonthName(end);
  const timeStr = event.time ? `<br/>${event.time}` : '';

  return `${sameMonth ? mon : `${mon} \u2013 ${emon}`}${timeStr}`;
};

export const ManchesterEvents = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [isPastExpanded, setIsPastExpanded] = useState(false);

  // Get upcoming and past events
  const upcomingEvents = useMemo(() => getUpcomingEvents(MANCHESTER_EVENTS), []);
  const pastEvents = useMemo(() => getPastEvents(MANCHESTER_EVENTS), []);

  // Track page view on mount
  useEffect(() => {
    trackEvent('Manchester Events - Page View', {
      totalEvents: upcomingEvents.length,
      pastEvents: pastEvents.length,
      hasPickEvent: !!getPickEvent(MANCHESTER_EVENTS),
    });
  }, [upcomingEvents.length, pastEvents.length]);

  // Get the featured pick event
  const pickEvent = useMemo(() => getPickEvent(MANCHESTER_EVENTS), []);

  // Determine which events to show based on filter
  const isShowingPast = activeFilter === 'past';

  // Filter events
  const filteredEvents = useMemo(() => {
    if (activeFilter === 'past') return pastEvents;
    if (activeFilter === 'all') return upcomingEvents;
    return upcomingEvents.filter(e => e.tags.includes(activeFilter as EventTag));
  }, [upcomingEvents, pastEvents, activeFilter]);

  // Filter past events by tag (for expandable section)
  const filteredPastEvents = useMemo(() => {
    if (activeFilter === 'all' || activeFilter === 'past') return pastEvents;
    return pastEvents.filter(e => e.tags.includes(activeFilter as EventTag));
  }, [pastEvents, activeFilter]);

  // Group by month
  const groupedEvents = useMemo(() => groupEventsByMonth(filteredEvents), [filteredEvents]);
  const groupedPastEvents = useMemo(() => groupEventsByMonth(filteredPastEvents), [filteredPastEvents]);

  // Split months: before/including October (month 9) and after
  const monthKeys = useMemo(() => Array.from(groupedEvents.keys()).sort(), [groupedEvents]);
  const pastMonthKeys = useMemo(() => Array.from(groupedPastEvents.keys()).sort((a, b) => b - a), [groupedPastEvents]);
  const splitIndex = monthKeys.findIndex(k => k % 100 > 9);
  const monthsBeforeCTA = splitIndex === -1 ? monthKeys : monthKeys.slice(0, splitIndex);
  const monthsAfterCTA = splitIndex === -1 ? [] : monthKeys.slice(splitIndex);

  // Count events per filter
  const filterCounts = useMemo(() => {
    const counts: Record<FilterType, number> = {
      all: upcomingEvents.length,
      past: pastEvents.length,
      careers: 0,
      founders: 0,
      ai: 0,
      community: 0,
      bhm: 0,
      cyber: 0,
      data: 0,
      healthtech: 0,
    };

    upcomingEvents.forEach(e => {
      e.tags.forEach(tag => {
        counts[tag]++;
      });
    });

    return counts;
  }, [upcomingEvents, pastEvents]);

  const handleFilterClick = (filter: FilterType) => {
    setActiveFilter(filter);
    if (filter === 'past') {
      setIsPastExpanded(false);
    }
    trackEvent('Manchester Events - Filter Click', {
      filter: filter,
      filterLabel: filter === 'past' ? 'Past Events' : FILTER_LABELS[filter as keyof typeof FILTER_LABELS],
      resultCount: filter === 'past' ? pastEvents.length : (filter === 'all' ? upcomingEvents.length : upcomingEvents.filter(e => e.tags.includes(filter as EventTag)).length),
    });
  };

  const handleTicketClick = (event: Event) => {
    trackEvent('Manchester Events - Ticket Click', {
      eventTitle: event.title,
      eventDate: event.start,
      eventLocation: event.where,
      eventTags: event.tags.join(', '),
      isFree: event.free || false,
      isPick: event.pick || false,
      destinationUrl: event.url,
    });
  };

  const handlePastToggle = () => {
    setIsPastExpanded(!isPastExpanded);
    trackEvent('Manchester Events - Past Events Toggle', {
      expanded: !isPastExpanded,
      pastEventsCount: pastEvents.length,
    });
  };

  // Format today's date
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // JSON-LD for all upcoming events
  const jsonLd = generateEventJsonLd(upcomingEvents);

  // Filters to show (only show filters with events)
  const filtersToShow: FilterType[] = ['all', 'careers', 'founders', 'ai', 'community', 'bhm', 'cyber', 'data', 'past'];

  return (
    <PageWrapper>
      <Helmet>
        <title>Manchester Tech Events 2026 | Renee Louise</title>
        <meta name="description" content="A hand-picked calendar of Manchester tech events for autumn 2026: AI conferences, cybersecurity, data events, BHM celebrations and community events." />
        <link rel="canonical" href="https://reneelouise.tech/resources/manchester-tech-events" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <main>
        <Wrap>
          {/* Hero Section */}
          <HeroSection>
            <div>
              <Eyebrow>Events · Autumn 2026</Eyebrow>
              <HeroTitle>Manchester tech events <em>worth your time.</em></HeroTitle>
            </div>
            <div>
              <HeroSub>
                A hand-picked calendar for people in the Manchester tech scene. AI conferences, cybersecurity events, data meetups, Black History Month celebrations and community networking.
              </HeroSub>
              <HeroMeta>
                <span><b>{upcomingEvents.length}</b> upcoming</span>
                <span>Updated <b>{formattedDate}</b></span>
              </HeroMeta>
            </div>
          </HeroSection>

          {/* Featured Pick - only show when not viewing past events */}
          {pickEvent && !isShowingPast && (
            <PickSection aria-labelledby="pick-title">
              <PickDate>
                <PickDay>{parseDate(pickEvent.start).getDate()}</PickDay>
                <PickMonth>
                  {getShortMonthName(parseDate(pickEvent.start))} · {getWeekdayShort(parseDate(pickEvent.start))}
                </PickMonth>
              </PickDate>
              <div>
                <PickLabel>
                  <Badge aria-hidden="true" />My pick this autumn
                </PickLabel>
                <PickTitle id="pick-title">{pickEvent.title}</PickTitle>
                <PickDesc>{pickEvent.desc}</PickDesc>
                <PickWhere>
                  <b>{pickEvent.time}</b> · {pickEvent.where} · {pickEvent.free && <b>Free</b>}
                </PickWhere>
              </div>
              <ButtonLight
                href={pickEvent.url}
                target="_blank"
                rel="noopener"
                onClick={() => trackEvent('Manchester Events - Featured Pick Click', {
                  eventTitle: pickEvent.title,
                  eventDate: pickEvent.start,
                  eventLocation: pickEvent.where,
                })}
              >
                Book a free place <span className="ar" aria-hidden="true">&rarr;</span>
              </ButtonLight>
            </PickSection>
          )}

          {/* Filters */}
          <FiltersContainer role="group" aria-label="Filter events">
            <FiltersLabel>Show</FiltersLabel>
            {filtersToShow.map(filter => (
              <Chip
                key={filter}
                $active={activeFilter === filter}
                aria-pressed={activeFilter === filter}
                onClick={() => handleFilterClick(filter)}
              >
                {filter === 'past' ? 'Past Events' : FILTER_LABELS[filter as keyof typeof FILTER_LABELS]}
                <ChipCount>{filterCounts[filter]}</ChipCount>
              </Chip>
            ))}
          </FiltersContainer>

          {/* Events before Guide CTA (September & October) */}
          <div id="cal-a">
            {filteredEvents.length === 0 ? (
              <EmptyMessage>No {isShowingPast ? 'past' : 'upcoming'} events in this category right now.</EmptyMessage>
            ) : (
              (isShowingPast ? pastMonthKeys : monthsBeforeCTA).map(monthKey => {
                const monthEvents = (isShowingPast ? groupedPastEvents : groupedEvents).get(monthKey) || [];
                return (
                  <MonthSection key={monthKey}>
                    <MonthHeading>
                      {getMonthName(monthKey)}
                      <span>{monthEvents.length} event{monthEvents.length !== 1 ? 's' : ''}</span>
                    </MonthHeading>
                    <EventsList>
                      {monthEvents.map((event, idx) => (
                        <EventRow key={`${event.start}-${event.title}`} $isFirst={idx === 0}>
                          <EventWhen>
                            {formatEventDate(event)}
                            <small dangerouslySetInnerHTML={{ __html: formatEventDateMeta(event) }} />
                          </EventWhen>
                          <div>
                            <EventTitle>
                              {event.title}
                              {event.pick && <EventStar>My pick</EventStar>}
                            </EventTitle>
                            <EventWhere>{event.where}</EventWhere>
                            <EventDesc>{event.desc}</EventDesc>
                            <EventTags>
                              {event.tags.map(tag => (
                                <Tag key={tag}>{TAG_LABELS[tag]}</Tag>
                              ))}
                              {event.free && <Tag $free>Free</Tag>}
                            </EventTags>
                          </div>
                          <TicketsButton
                            href={event.url}
                            target="_blank"
                            rel="noopener"
                            onClick={() => handleTicketClick(event)}
                          >
                            {isShowingPast ? 'View' : 'Tickets'} <ArrowUpRight size={14} color="#D9442B" />
                          </TicketsButton>
                        </EventRow>
                      ))}
                    </EventsList>
                  </MonthSection>
                );
              })
            )}
          </div>

          {/* Guide CTA Section - only show when not viewing past events */}
          {!isShowingPast && (
          <GuideCTASection id="guide-cta" aria-labelledby="guide-title">
            <div>
              <Eyebrow>Going to an event?</Eyebrow>
              <GuideCTATitle id="guide-title">Don't just attend. <em>Walk out with a plan.</em></GuideCTATitle>
              <GuideCTAText>
                Most people go to events, collect a few LinkedIn follows and never hear back. <i>The Unhinged Guide to Breaking Into Tech</i> shows you what to do before, during and after, so a conversation turns into an opportunity.
              </GuideCTAText>
              <GuideCTAList>
                <GuideCTAItem>
                  <b>01</b>
                  <span>Networking message templates, including the follow-up after a talk or panel</span>
                </GuideCTAItem>
                <GuideCTAItem>
                  <b>02</b>
                  <span>22 fillable worksheets to work out what you want and prove you can do it</span>
                </GuideCTAItem>
                <GuideCTAItem>
                  <b>03</b>
                  <span>A 30-day action plan so you know exactly what to do next</span>
                </GuideCTAItem>
                <GuideCTAItem>
                  <b>04</b>
                  <span>An AI prompt library for research, CVs and interview prep</span>
                </GuideCTAItem>
              </GuideCTAList>
              <BuyRow>
                <Link
                  to="/the-guide"
                  onClick={() => trackEvent('Manchester Events - Guide CTA Click', {
                    price: currentPrice,
                    originalPrice: futurePrice,
                    source: 'manchester-events-page-mid-list',
                  })}
                  style={{ textDecoration: 'none' }}
                >
                  <Button as="span">Get the guide <ArrowRight size={14} color="#D9442B" /></Button>
                </Link>
                <PriceBlock>
                  <PriceNow>£{currentPrice}</PriceNow>
                  <PriceWas>£{futurePrice}</PriceWas>
                  <PriceLabel>Founding<br />price</PriceLabel>
                </PriceBlock>
              </BuyRow>
            </div>
            <GuideImage>
              <img
                src={guideHero}
                width="520"
                height="664"
                alt="The Unhinged Guide to Breaking Into Tech, with the toolkit behind it"
                loading="lazy"
              />
            </GuideImage>
          </GuideCTASection>
          )}

          {/* Events after Guide CTA (November & December) - only show when not viewing past events */}
          {!isShowingPast && (
          <div id="cal-b">
            {monthsAfterCTA.map(monthKey => {
              const monthEvents = groupedEvents.get(monthKey) || [];
              return (
                <MonthSection key={monthKey}>
                  <MonthHeading>
                    {getMonthName(monthKey)}
                    <span>{monthEvents.length} event{monthEvents.length !== 1 ? 's' : ''}</span>
                  </MonthHeading>
                  <EventsList>
                    {monthEvents.map((event, idx) => (
                      <EventRow key={`${event.start}-${event.title}`} $isFirst={idx === 0}>
                        <EventWhen>
                          {formatEventDate(event)}
                          <small dangerouslySetInnerHTML={{ __html: formatEventDateMeta(event) }} />
                        </EventWhen>
                        <div>
                          <EventTitle>
                            {event.title}
                            {event.pick && <EventStar>My pick</EventStar>}
                          </EventTitle>
                          <EventWhere>{event.where}</EventWhere>
                          <EventDesc>{event.desc}</EventDesc>
                          <EventTags>
                            {event.tags.map(tag => (
                              <Tag key={tag}>{TAG_LABELS[tag]}</Tag>
                            ))}
                            {event.free && <Tag $free>Free</Tag>}
                          </EventTags>
                        </div>
                        <TicketsButton
                          href={event.url}
                          target="_blank"
                          rel="noopener"
                          onClick={() => handleTicketClick(event)}
                        >
                          Tickets <ArrowUpRight size={14} color="#D9442B" />
                        </TicketsButton>
                      </EventRow>
                    ))}
                  </EventsList>
                </MonthSection>
              );
            })}
          </div>
          )}

          {/* Past Events Expandable Section - only show when not already viewing past filter */}
          {!isShowingPast && filteredPastEvents.length > 0 && (
            <PastEventsSection>
              <PastEventsHeader
                $isExpanded={isPastExpanded}
                onClick={handlePastToggle}
                aria-expanded={isPastExpanded}
                aria-controls="past-events-content"
              >
                <PastEventsTitle>
                  Past Events
                  <span>{filteredPastEvents.length} event{filteredPastEvents.length !== 1 ? 's' : ''}</span>
                </PastEventsTitle>
                <PastEventsToggle $isExpanded={isPastExpanded}>
                  {isPastExpanded ? 'Hide' : 'Show'}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </PastEventsToggle>
              </PastEventsHeader>
              <PastEventsContent $isExpanded={isPastExpanded} id="past-events-content">
                {filteredPastEvents.map((event) => (
                  <PastEventRow key={`${event.start}-${event.title}`}>
                    <EventWhen>
                      {formatEventDate(event)}
                      <small dangerouslySetInnerHTML={{ __html: formatEventDateMeta(event) }} />
                    </EventWhen>
                    <div>
                      <EventTitle>{event.title}</EventTitle>
                      <EventWhere>{event.where}</EventWhere>
                      <EventTags>
                        {event.tags.map(tag => (
                          <Tag key={tag}>{TAG_LABELS[tag]}</Tag>
                        ))}
                      </EventTags>
                    </div>
                    <TicketsButton
                      href={event.url}
                      target="_blank"
                      rel="noopener"
                      onClick={() => handleTicketClick(event)}
                    >
                      View <ArrowUpRight size={14} color="#D9442B" />
                    </TicketsButton>
                  </PastEventRow>
                ))}
              </PastEventsContent>
            </PastEventsSection>
          )}
        </Wrap>

        {/* Bragbooq Section */}
        <BragSection aria-labelledby="brag-title">
          <BragInner>
            <div>
              <Eyebrow>After the event</Eyebrow>
              <BragTitle id="brag-title">
                Met someone great? Spoke on a panel? <em>Log it before you forget.</em>
              </BragTitle>
              <BragText>
                Bragbooq is the career evidence platform I built. It captures your wins as they happen, from the talk you gave to the project you shipped, so they're ready when you update your CV, go for a promotion or apply for your next role.
              </BragText>
              <BragRow>
                <ButtonLight
                  href={BRAGBOOQ_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackEvent('Manchester Events - Bragbooq CTA Click', {
                    clickType: 'button',
                    destinationUrl: BRAGBOOQ_URL,
                  })}
                >
                  Try Bragbooq <span className="ar" aria-hidden="true">&rarr;</span>
                </ButtonLight>
                <BragTextLink
                  href={BRAGBOOQ_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackEvent('Manchester Events - Bragbooq Link Click', {
                    clickType: 'text-link',
                    destinationUrl: BRAGBOOQ_URL,
                  })}
                >
                  Built by me, used by 100+ people
                </BragTextLink>
              </BragRow>
            </div>
            <figure style={{ margin: 0 }}>
              <BragShot>
                <img
                  src={bragbooqDashboard}
                  width="3022"
                  height="1642"
                  alt="The Bragbooq dashboard showing recent wins captured from GitHub"
                  loading="lazy"
                />
              </BragShot>
            </figure>
          </BragInner>
        </BragSection>

        {/* Footer Note */}
        <Wrap>
          <FootNote>
            <div>
              <FootNoteTitle>Know an event that <em>should be here?</em></FootNoteTitle>
              <FootNoteText>Send it over and I'll take a look.</FootNoteText>
              <TextLink
                href="mailto:hello@reneelouise.tech?subject=Manchester%20Event%20suggestion"
                onClick={() => trackEvent('Manchester Events - Suggest Event Click', {
                  activeFilter: activeFilter,
                  totalEventsShown: filteredEvents.length,
                })}
              >
                Suggest an event
              </TextLink>
            </div>
            <div>
              <FootNoteSmall>
                Dates, prices and details come from each organiser and can change. Always check the event page before you book. I'm not affiliated with these events unless I say so.
              </FootNoteSmall>
            </div>
          </FootNote>
          <SiteFooter>
            <span>© 2026 Renee Louise</span>
            <Link to="/">reneelouise.tech</Link>
          </SiteFooter>
        </Wrap>
      </main>
    </PageWrapper>
  );
};
